import { useEffect, useState } from 'react'
import { signalsAPI } from '../../services/api'
import type { EngineActivityEvent } from '../../types'
import { parseApiDate } from '../../utils/signalExpiry'

export const ACTIVITY_POLL_MS = 60_000
// Enough history that a long stand-down still shows where it began once the
// identical rows are folded into one. The endpoint allows up to 100.
export const ACTIVITY_LIMIT = 50

// The scan's risk-gate reasons (backend tasks/signal_tasks.py SKIP_*).
export const STAND_DOWN_REASONS: Record<string, string> = {
  portfolio_heat_cap: 'portfolio heat cap',
  max_open_platform_signals: 'open-position cap',
}

export const standDownLabel = (outcome: string) =>
  STAND_DOWN_REASONS[outcome] ?? outcome.replace(/_/g, ' ')

const isStandDown = (event: EngineActivityEvent) =>
  event.kind === 'scan' && event.outcome != null && event.outcome !== 'dispatched'

// A scan that ran and opened nothing ("scan · 0 new").
const isEmptyScan = (event: EngineActivityEvent) =>
  event.kind === 'scan' && event.outcome === 'dispatched' && event.new_signals === 0

// Two scans fold into one row when both stood down for the same reason, or
// both ran and opened nothing.
const foldsWith = (a: EngineActivityEvent, b: EngineActivityEvent) =>
  (isStandDown(a) && isStandDown(b) && a.outcome === b.outcome) || (isEmptyScan(a) && isEmptyScan(b))

export interface EngineActivityState {
  /** Newest first; null until the first load. */
  events: EngineActivityEvent[] | null
  failed: boolean
  /** When the events were fetched, for the "12m ago" labels. */
  now: number
}

/**
 * The engine's scans and closes, polled. One fetch serves the banner and the
 * feed. ``enabled`` false (the paper view) fetches nothing.
 */
export const useEngineActivity = (enabled = true): EngineActivityState => {
  const [state, setState] = useState<EngineActivityState>({ events: null, failed: false, now: Date.now() })

  useEffect(() => {
    if (!enabled) return
    let active = true
    let loaded = false

    const load = async () => {
      if (loaded && document.hidden) return
      try {
        const data = await signalsAPI.engineActivity(ACTIVITY_LIMIT)
        if (!active) return
        loaded = true
        setState({ events: data.events, failed: false, now: Date.now() })
      } catch (error) {
        if (!active) return
        console.error('Engine activity refresh failed:', error)
        setState((prev) => ({ ...prev, failed: true }))
      }
    }

    load()
    const timer = setInterval(load, ACTIVITY_POLL_MS)
    return () => {
      active = false
      clearInterval(timer)
    }
  }, [enabled])

  return state
}

/** One feed row: an event, or a run of identical stand-downs or empty scans folded into one. */
export interface ActivityRow {
  /** The newest event of the run. */
  event: EngineActivityEvent
  count: number
  /** The oldest event of the run (the event itself when count is 1). */
  first: EngineActivityEvent
}

/**
 * Folds consecutive scans that stood down for the same reason, and consecutive
 * scans that ran and opened nothing, into one row. Closes and scans that
 * opened signals are distinct events and always keep their own row; a close
 * between two foldable scans ends the run.
 */
export const groupActivity = (events: EngineActivityEvent[]): ActivityRow[] => {
  const rows: ActivityRow[] = []
  for (const event of events) {
    const last = rows[rows.length - 1]
    if (last && foldsWith(last.event, event)) {
      last.count += 1
      last.first = event
    } else {
      rows.push({ event, count: 1, first: event })
    }
  }
  return rows
}

export interface StandDownStreak {
  /** The risk-gate reason every scan in the streak gave. */
  reason: string
  scans: number
  /** The oldest stand-down in the streak that the fetched history reaches. */
  since: Date
  lastScan: Date
}

/**
 * The newest scans, when two or more in a row all stood down for the same
 * reason. Closes between them don't break it: they are not scans. null when
 * the latest scan dispatched, or only one stood down.
 *
 * ``since`` is as far back as the fetched history goes, so "every scan since
 * <since> stood down" holds even when the streak began earlier.
 */
export const standDownStreak = (events: EngineActivityEvent[]): StandDownStreak | null => {
  const scans = events.filter((event) => event.kind === 'scan')
  const latest = scans[0]
  if (!latest || !isStandDown(latest)) return null
  let n = 0
  while (n < scans.length && scans[n].outcome === latest.outcome) n += 1
  if (n < 2) return null
  return {
    reason: latest.outcome as string,
    scans: n,
    since: parseApiDate(scans[n - 1].at),
    lastScan: parseApiDate(latest.at),
  }
}
